/**
 * Row codec — bridges consumer payloads to the on-disk shape.
 *
 * When encryption is off the codec is the SYNCHRONOUS identity function. This
 * matters: awaiting an async identity would yield a microtask, and IndexedDB
 * auto-commits a transaction once its task queue drains with no pending
 * request — so any `await` between two requests on the same tx throws
 * `TransactionInactiveError`. Keeping the no-crypto path synchronous lets the
 * multi-store transaction helper issue back-to-back requests safely.
 *
 * When encryption is on the payload body is sealed into an
 * {@link EncryptedEnvelope} while the primary key and the bookkeeping columns
 * (`__version`, `__updatedAt`) stay in clear text so IndexedDB key lookups,
 * secondary indexes built on the key path, and the
 * `@norkron/web-sync-queue` outbox keep functioning without the passphrase.
 * Encrypted encode/decode are async; callers run them OUTSIDE the IDB
 * transaction window.
 */

import { isEncryptedEnvelope, openPayload, sealPayload } from './encryption'
import type { EncryptedEnvelope } from './encryption'

/** Encode/decode may be sync (identity) or async (crypto). */
export type EncodeFn = (row: unknown) => unknown | Promise<unknown>
export type DecodeFn = (raw: unknown) => unknown | Promise<unknown>

export interface Codec {
	readonly encode: EncodeFn
	readonly decode: DecodeFn
	/** True when encode/decode are async (encryption enabled). */
	readonly async: boolean
}

interface ClearRow {
	__version: number
	__updatedAt: number
	[k: string]: unknown
}

/**
 * Build a codec for one table. `keyPath` and the two `__` bookkeeping columns
 * are hoisted out of the encrypted envelope so they remain queryable.
 */
export function makeCodec(keyPath: string, key: CryptoKey | null): Codec {
	if (!key) {
		const identity = (v: unknown) => v
		return { encode: identity, decode: identity, async: false }
	}

	const encode: EncodeFn = async (row) => {
		const r = row as ClearRow
		const { [keyPath]: pk, __version, __updatedAt, ...body } = r
		const env = await sealPayload(key, body)
		return { [keyPath]: pk, __version, __updatedAt, __body: env }
	}

	const decode: DecodeFn = async (raw) => {
		const r = raw as ClearRow & { __body?: EncryptedEnvelope }
		if (!r || !isEncryptedEnvelope(r.__body)) return raw
		const { __body, ...clear } = r
		const body = (await openPayload(key, __body)) as Record<string, unknown>
		return { ...body, ...clear }
	}

	return { encode, decode, async: true }
}
